import { cloneElement, useCallback, useEffect, useId, useRef, useState } from 'react'
import type {
  FocusEvent as ReactFocusEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
  Ref,
} from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../../lib/cn'
import { computePosition } from './floating'
import type { Placement } from './floating'

export interface TooltipProps {
  /** The label. Keep it to a few words — a tooltip is a name, not documentation. */
  content: ReactNode
  placement?: Placement
  /** Delay before showing on hover. Focus always shows immediately. */
  delayMs?: number
  disabled?: boolean
  children: ReactElement<{
    ref?: Ref<HTMLElement>
    'aria-describedby'?: string
    onPointerEnter?: (event: ReactPointerEvent) => void
    onPointerLeave?: (event: ReactPointerEvent) => void
    onFocus?: (event: ReactFocusEvent) => void
    onBlur?: (event: ReactFocusEvent) => void
  }>
}

/**
 * Names an icon-only control. It never carries information that exists nowhere else —
 * the nav rail's tooltips repeat labels a screen reader already reads from `aria-label`.
 */
export const Tooltip = ({
  content,
  placement = 'right',
  delayMs = 220,
  disabled = false,
  children,
}: TooltipProps): ReactElement => {
  const id = useId()
  const anchorRef = useRef<HTMLElement | null>(null)
  const floatingRef = useRef<HTMLDivElement | null>(null)
  const timerRef = useRef<number | null>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const show = useCallback(
    (immediate: boolean) => {
      if (disabled) return
      clearTimer()
      if (immediate) {
        setOpen(true)
        return
      }
      timerRef.current = window.setTimeout(() => {
        setOpen(true)
      }, delayMs)
    },
    [clearTimer, delayMs, disabled],
  )

  const hide = useCallback(() => {
    clearTimer()
    setOpen(false)
  }, [clearTimer])

  useEffect(() => clearTimer, [clearTimer])

  /**
   * The child may already carry a ref of its own — a menu trigger hands one to `Popover` so the
   * panel knows what to hang off. `cloneElement` *replaces* whatever it is given, so the tooltip
   * has to hold both: its own anchor and the caller's. Overwriting the caller's ref left it null,
   * and a popover with a null anchor renders where nothing can see it.
   */
  const childRef = children.props.ref
  const setAnchor = useCallback(
    (node: HTMLElement | null): void => {
      anchorRef.current = node
      if (typeof childRef === 'function') {
        childRef(node)
        return
      }
      if (childRef) childRef.current = node
    },
    [childRef],
  )

  useEffect(() => {
    if (!open) return
    const anchor = anchorRef.current
    const floating = floatingRef.current
    if (!anchor || !floating) return

    const update = (): void => {
      const next = computePosition(
        anchor.getBoundingClientRect(),
        { width: floating.offsetWidth, height: floating.offsetHeight },
        placement,
        'center',
        8,
      )
      setPosition({ top: next.top, left: next.left })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') hide()
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, placement, hide])

  const trigger = cloneElement(children, {
    ref: setAnchor,
    'aria-describedby': open ? id : undefined,
    onPointerEnter: (event: ReactPointerEvent) => {
      children.props.onPointerEnter?.(event)
      if (event.pointerType !== 'touch') show(false)
    },
    onPointerLeave: (event: ReactPointerEvent) => {
      children.props.onPointerLeave?.(event)
      hide()
    },
    onFocus: (event: ReactFocusEvent) => {
      children.props.onFocus?.(event)
      show(true)
    },
    onBlur: (event: ReactFocusEvent) => {
      children.props.onBlur?.(event)
      hide()
    },
  })

  return (
    <>
      {trigger}
      {open && !disabled
        ? createPortal(
            <div
              ref={floatingRef}
              id={id}
              role="tooltip"
              style={{ top: position.top, left: position.left }}
              className={cn(
                'anim-fade pointer-events-none fixed z-70 max-w-56 rounded-sm',
                'bg-chrome-700 px-2 py-1 text-chrome-ink shadow-float',
                't-small',
              )}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
